import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useParams } from 'react-router-dom';

import { externalLinkOpen } from '../../../../actions/externallink';
import { RedirectServices, getRedirectUrl } from '../../../../config/urls';
import useMergeProps from '../../../../hooks/useMergeProps';
import useModalContext from '../../../../hooks/useModalContext';
import { StateType } from '../../../../states';
import RoutesRedirectionFactory from '../../../../utils/routes/RoutesRedirectionFactory';
import useOpenGRView from '../../hooks/useOpenGRView';
import { selectGRGlobalSkinId } from '../../selectors/global/globalPrimary.selector';
import { selectIsGREnabledAndNotForbidden } from '../../selectors/runtime.selector';
import { selectGRUserRoundId } from '../../selectors/user/userPrimary.selector';
import { ObfGRRounds, ReverseObfGRRounds, ReverseObfGRRoundsKeyType } from '../../types/round';

import GRRoundRules from './GRRoundRules';
import selectGRRoundRulesProps from './GRRoundRules.selector';

type GRRoundRulesContainerProps = {
  className?: string;
};

type Params = {
  obfRoundId: ReverseObfGRRoundsKeyType;
};

const GRRoundRulesContainer = (ownProps: GRRoundRulesContainerProps) => {
  const dispatch = useDispatch();
  const { setOnBackButtonClick } = useModalContext();
  const openGRView = useOpenGRView();
  const { obfRoundId } = useParams<Params>();
  const skinId = useSelector(selectGRGlobalSkinId);

  const onBackButtonClick = useCallback(
    () => () => {
      openGRView();
    },
    [openGRView],
  );

  useEffect(() => {
    if (setOnBackButtonClick) {
      setOnBackButtonClick(onBackButtonClick);

      return () => {
        setOnBackButtonClick(null);
      };
    }
  }, [setOnBackButtonClick, onBackButtonClick]);

  const onFullRulesClick = useCallback(() => {
    dispatch(externalLinkOpen(getRedirectUrl(RedirectServices.GR_RULES, { skinId })));
  }, [dispatch, skinId]);

  const otherProps = useMemo(
    () => ({
      roundId: ReverseObfGRRounds[obfRoundId],
      onFullRulesClick,
    }),
    [obfRoundId, onFullRulesClick],
  );

  const selectedProps = useSelector((state: StateType) => selectGRRoundRulesProps(state, otherProps));

  const enhancedProps = useMergeProps({ ownProps, otherProps, selectedProps });

  const userRoundId = useSelector(selectGRUserRoundId);
  const isGREnabled = useSelector(selectIsGREnabledAndNotForbidden);

  if (isGREnabled && userRoundId !== undefined) {
    if (ObfGRRounds[userRoundId] < parseInt(obfRoundId)) {
      return <Redirect to={RoutesRedirectionFactory.getGRRoutePathname()} />;
    }

    const { minStakeMultiplicator, maxStakeMultiplicator } = enhancedProps;

    if (minStakeMultiplicator !== undefined && maxStakeMultiplicator !== undefined) {
      return (
        <GRRoundRules
          {...enhancedProps}
          minStakeMultiplicator={minStakeMultiplicator}
          maxStakeMultiplicator={maxStakeMultiplicator}
        />
      );
    }
  }
  return <Redirect to={RoutesRedirectionFactory.getHomeRoutePathname()} />;
};

export default GRRoundRulesContainer;
